import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Plus, Edit, Trash2, Save, X, TestTube } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface TagMapping {
  id: string;
  tag_name: string;
  tag_id: string;
  license_id: string;
  priority_order: number;
  is_active: boolean;
  license?: {
    name: string;
  };
}

interface License {
  id: string;
  name: string;
}

export const TagMappingManagement = () => {
  const [tagMappings, setTagMappings] = useState<TagMapping[]>([]);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMapping, setEditingMapping] = useState<TagMapping | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [testTags, setTestTags] = useState("");
  const [testResult, setTestResult] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    Promise.all([fetchTagMappings(), fetchLicenses()]);
  }, []);

  const fetchTagMappings = async () => {
    try {
      const { data, error } = await supabase
        .from('keap_tag_mappings')
        .select(`
          *,
          license:licenses(name)
        `)
        .order('priority_order');

      if (error) throw error;
      setTagMappings(data || []);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch tag mappings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLicenses = async () => {
    try {
      const { data, error } = await supabase
        .from('licenses')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setLicenses(data || []);
    } catch (error) {
      console.error('Failed to fetch licenses:', error);
    }
  };

  const saveMapping = async () => {
    if (!editingMapping) return;

    try {
      const mappingData = {
        tag_name: editingMapping.tag_name,
        tag_id: editingMapping.tag_id,
        license_id: editingMapping.license_id,
        priority_order: editingMapping.priority_order,
        is_active: editingMapping.is_active
      };

      if (editingMapping.id === 'new') {
        const { error } = await supabase
          .from('keap_tag_mappings')
          .insert([mappingData]);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('keap_tag_mappings')
          .update(mappingData)
          .eq('id', editingMapping.id);
        if (error) throw error;
      }

      toast({
        title: "Success",
        description: `Tag mapping ${editingMapping.id === 'new' ? 'created' : 'updated'} successfully`,
      });

      setIsDialogOpen(false);
      setEditingMapping(null);
      fetchTagMappings();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save tag mapping",
        variant: "destructive",
      });
    }
  };

  const deleteMapping = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tag mapping?')) return;

    try {
      const { error } = await supabase
        .from('keap_tag_mappings')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Tag mapping deleted successfully",
      });

      fetchTagMappings();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete tag mapping",
        variant: "destructive",
      });
    }
  };

  const startEditing = (mapping: TagMapping | null = null) => {
    if (mapping) {
      setEditingMapping(mapping);
    } else {
      setEditingMapping({
        id: 'new',
        tag_name: '',
        tag_id: '',
        license_id: '',
        priority_order: tagMappings.length + 1,
        is_active: true
      });
    }
    setIsDialogOpen(true);
  };

  const simulateTagMapping = () => {
    if (!testTags.trim()) return;

    const inputTags = testTags.split(',').map(tag => tag.trim().toLowerCase());
    
    // Find the highest priority mapping that matches any of the input tags
    const activeTagMappings = tagMappings
      .filter(mapping => mapping.is_active)
      .sort((a, b) => a.priority_order - b.priority_order);

    for (const mapping of activeTagMappings) {
      if (inputTags.includes(mapping.tag_name.toLowerCase())) {
        const license = licenses.find(l => l.id === mapping.license_id);
        setTestResult(`License: ${license?.name || 'Unknown'} (Priority: ${mapping.priority_order})`);
        return;
      }
    }

    setTestResult('No matching license found - would default to Free Trial');
  };

  if (loading) {
    return <div className="text-center">Loading tag mappings...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Tag Mapping Management</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => startEditing()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Tag Mapping
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingMapping?.id === 'new' ? 'Create Tag Mapping' : 'Edit Tag Mapping'}
              </DialogTitle>
              <DialogDescription>
                Map Keap tags to license levels with priority ordering
              </DialogDescription>
            </DialogHeader>
            
            {editingMapping && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Tag Name</label>
                  <Input
                    value={editingMapping.tag_name}
                    onChange={(e) => setEditingMapping({
                      ...editingMapping,
                      tag_name: e.target.value
                    })}
                    placeholder="Tag name from Keap"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Tag ID (Optional)</label>
                  <Input
                    value={editingMapping.tag_id}
                    onChange={(e) => setEditingMapping({
                      ...editingMapping,
                      tag_id: e.target.value
                    })}
                    placeholder="Keap tag ID"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">License</label>
                  <Select
                    value={editingMapping.license_id}
                    onValueChange={(value) => setEditingMapping({
                      ...editingMapping,
                      license_id: value
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a license" />
                    </SelectTrigger>
                    <SelectContent>
                      {licenses.map((license) => (
                        <SelectItem key={license.id} value={license.id}>
                          {license.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium">Priority Order</label>
                  <Input
                    type="number"
                    value={editingMapping.priority_order}
                    onChange={(e) => setEditingMapping({
                      ...editingMapping,
                      priority_order: parseInt(e.target.value) || 1
                    })}
                    placeholder="1 = highest priority"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button onClick={saveMapping}>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Test Simulator */}
      <Card>
        <CardHeader>
          <CardTitle>Tag Mapping Simulator</CardTitle>
          <CardDescription>
            Test which license level would be assigned for given tags
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2">
            <Input
              value={testTags}
              onChange={(e) => setTestTags(e.target.value)}
              placeholder="Enter comma-separated tags (e.g., pro_user, premium, trial)"
              className="flex-1"
            />
            <Button onClick={simulateTagMapping}>
              <TestTube className="h-4 w-4 mr-2" />
              Test
            </Button>
          </div>
          {testResult && (
            <div className="mt-2">
              <Badge variant="outline">{testResult}</Badge>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tag Mappings</CardTitle>
          <CardDescription>
            Configure how Keap tags map to license levels (lower priority order = higher precedence)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Priority</TableHead>
                <TableHead>Tag Name</TableHead>
                <TableHead>Tag ID</TableHead>
                <TableHead>License</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tagMappings.map((mapping) => (
                <TableRow key={mapping.id}>
                  <TableCell>
                    <Badge variant="outline">{mapping.priority_order}</Badge>
                  </TableCell>
                  <TableCell className="font-medium">{mapping.tag_name}</TableCell>
                  <TableCell className="text-muted-foreground">{mapping.tag_id || 'N/A'}</TableCell>
                  <TableCell>{mapping.license?.name}</TableCell>
                  <TableCell>
                    <Badge variant={mapping.is_active ? "default" : "secondary"}>
                      {mapping.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEditing(mapping)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteMapping(mapping.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};